<script setup lang="ts">
  import { computed, useCssModule } from 'vue';

  import { t } from '../../locale';
  import Chip from '../Chip/Chip.vue';
  import Icon from '../Icon/Icon.vue';

  export interface FilterChipProps {
    /**
     * Used for conditionally rendering filter count chip
     */
    filterCount?: number;

    /**
     * Use for conditionally rendering caret
     */
    hasDropdown?: boolean;

    /**
     * Used for styling when `hasDropdown` is true
     */
    isDropdownOpen?: boolean;

    /**
     * Used for conditionally rendering close icon and exposing remove event
     */
    isRemovable?: boolean;

    /**
     * Used for conditionally applying styles
     */
    isSelected?: boolean;
  }

  const props = withDefaults(defineProps<FilterChipProps>(), {
    filterCount: undefined,
    hasDropdown: false,
    isDropdownOpen: false,
    isRemovable: false,
    isSelected: false,
  });

  const emit = defineEmits<{
    (e: 'click'): void;
    (e: 'remove'): void;
  }>();

  const classes = useCssModule();
  const hasFilterCount = computed(() => Number(props.filterCount) > 0);

  function handleClick() {
    emit('click');
  }

  function handleRemove() {
    emit('remove');
  }
</script>

<template>
  <Chip
    should-override-colors
    tabindex="0"
    class="stash-filter-chip text-sm font-normal normal-case leading-5 text-ice-900"
    radius="pill"
    :class="[
      classes.root,
      {
        [classes['is-selected']]: props.isSelected,
        [classes['is-removable']]: props.isRemovable,
        [classes['is-dropdown-open']]: props.isDropdownOpen,
        [classes['has-dropdown']]: props.hasDropdown,
        [classes['has-filter-count']]: hasFilterCount,
      },
    ]"
    :is-removable="props.isSelected && props.isRemovable && !props.hasDropdown"
    @click="handleClick"
    @remove="handleRemove"
  >
    <div class="flex items-center">
      <!-- @slot default -->
      <slot></slot>

      <span
        v-if="hasFilterCount"
        data-test="span|filter-count"
        :class="classes['filter-count']"
        :aria-label="t('ll.numberOfActiveFilters')"
        :title="t('ll.numberOfActiveFilters')"
      >
        {{ props.filterCount }}
      </span>

      <span v-if="props.hasDropdown">
        <Icon
          data-test="icon|caret"
          name="caret-down"
          :class="[
            classes.caret,
            {
              [classes['caret-rotate']]: props.isDropdownOpen,
            },
          ]"
        />
      </span>
    </div>
  </Chip>
</template>

<style module>
  @reference "../../../styles/main.css";

  @layer utilities {
    /**
      many styles declared here are overriding styles defined in Chip
    */
    .root {
      background-color: var(--color-white);
      border: 1px solid var(--color-ice-500);
      cursor: pointer;
      height: --spacing(9);
      padding-left: --spacing(3);
      padding-right: --spacing(3);
    }

    .root:focus {
      box-shadow: 0 0 0 4px rgb(0 123 255 / 15%);
      outline: none;
    }

    .root:hover {
      background-color: var(--color-blue-100);
      border: 1px solid var(--color-blue-500);
    }

    .root.is-selected {
      color: var(--color-white);
      background-color: var(--color-blue-500);
      border: 1px solid var(--color-blue-500);
    }

    .root.is-selected:hover {
      color: var(--color-white);
      background-color: var(--color-blue-700);
      border: 1px solid var(--color-blue-700);
    }

    .root.is-selected.is-removable {
      background-color: var(--color-blue-500);
    }

    .root.is-selected.is-removable :global(.stash-chip__remove-button) {
      margin-right: -1px;
      padding-right: --spacing(3);
    }

    .root.is-selected.is-removable :global(.stash-chip__remove-button):hover {
      background-color: var(--color-blue-700);
    }

    .root.is-selected.has-filter-count:hover {
      background-color: var(--color-blue-700);
      color: var(--color-white);
    }

    .root.has-dropdown {
      padding-right: --spacing(1.5);
    }

    .root.has-filter-count:hover {
      background-color: var(--color-blue-100);
      color: var(--color-ice-900);
    }

    .root label {
      cursor: pointer;
    }

    .filter-count {
      display: inline-flex;
      align-items: center;
      border-radius: 5em;
      height: 16px;
      padding: 0 8px;
      margin-left: --spacing(1.5);
      font-size: 10px;
      font-weight: bold;
      background-color: var(--color-blue-500);
      color: var(--color-white);
    }

    .is-selected .filter-count {
      background-color: var(--color-blue-700);
    }

    .is-selected:hover .filter-count {
      background-color: var(--color-white);
      color: var(--color-blue-700);
    }

    .is-selected.is-removable .filter-count {
      background-color: var(--color-blue-700);
      color: var(--color-white);
    }

    .caret {
      color: var(--color-ice-700);
      margin-left: --spacing(1.5);
      transform: rotate(0);
      transition: all 0.5s;
    }

    .is-selected .caret {
      color: var(--color-white);
    }

    .caret-rotate {
      transform: rotate(-180deg);
    }
  }
</style>
